import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '../../../../components/Button/Button';
import axiosInstant from '../../../../api';
import { deleteApiKey } from '../../../../api/endpoints';
import toast from 'react-hot-toast';

interface ApiKey {
  id: string;
  key_prefix: string;
  label: string | null;
}

interface Props {
  open: boolean;
  apiKey: ApiKey;
  onClose: () => void;
  onDeleted: () => void;
  // Personal API Keys pass their own endpoint; Public API Keys omit it.
  deleteUrl?: (keyId: string) => string;
}

export const DeleteApiKeyDialog: React.FC<Props> = ({
  open,
  apiKey,
  onClose,
  onDeleted,
  deleteUrl = deleteApiKey,
}) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axiosInstant.delete(deleteUrl(apiKey.id));
      toast.success('API key revoked');
      onDeleted();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || 'Failed to revoke API key'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: 'auto' },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <Typography variant="h5" className="text-[20px]">
            Revoke API Key
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon className="text-text-2" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="pb-4 flex flex-col gap-3">
          <Typography className="text-text-2 text-[16px] leading-[24px]">
            This will immediately revoke the API key{' '}
            {/* `gray-100` does not exist in this Tailwind config (the `gray`
                key is a single literal), so this chip renders transparent
                today; bg-surface-2 would newly paint it. Left as-is. */}
            <code className="bg-gray-100 px-1 rounded text-[13px]">
              {apiKey.key_prefix}••••
            </code>
            {apiKey.label && (
              <>
                {' '}
                ({apiKey.label})
              </>
            )}
            .
          </Typography>
          <Typography className="text-down text-[14px] leading-[20px]">
            Any integrations using this key will immediately stop working.
            This action cannot be undone.
          </Typography>
        </div>
        <hr className="border-t border-border-token" />
      </DialogContent>

      <DialogActions className="!px-6 !py-4">
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {/* Saturated destructive fill stays literal: --down is the danger TEXT
            value and has no hover partner for a solid button. */}
        <Button
          variant="contained"
          onClick={handleDelete}
          disabled={loading}
          className="!bg-[#d32f2f] !text-white hover:!bg-[#b71c1c]"
        >
          {loading ? 'Revoking...' : 'Revoke Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
